import React from 'react';
import {
  ChevronRight,
  Users,
  Zap,
  Brain,
  CheckCircle,
  Search,
  Award,
  Workflow,
  Rocket,
  Shield,
  ArrowRight
} from 'lucide-react';

import { motion } from 'framer-motion';

const fadeInVariant = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const Features = () => {
  const features = [
    {
      icon: <Brain className="w-12 h-12 text-purple-600" />,
      title: "Intelligent Candidate Matching",
      description: "Harness the power of AI to match candidates with job roles, ensuring the best fit for your organization.",
      benefits: [
        "95% matching accuracy with ML-powered algorithms",
        "Smart skill gap analysis and recommendations",
        "Personality and culture fit assessment"
      ],
      highlight: "Reduces mis-hires by 75%"
    },
    {
      icon: <Workflow className="w-12 h-12 text-green-600" />,
      title: "Pipeline Automation",
      description: "Streamline the recruitment process with automated pipelines, from job posting to candidate onboarding.",
      benefits: [
        "Customizable workflow templates for different roles",
        "Automated email sequences and follow-ups",
        "Integration with major job boards and ATS systems"
      ],
      highlight: "Saves 15 hours per hire"
    },
    {
      icon: <Shield className="w-12 h-12 text-blue-600" />,
      title: "Comprehensive Assessments",
      description: "Evaluate candidates on skills, language proficiency, emotional intelligence, and cultural fit.",
      benefits: [
        "Multi-format technical assessment platform",
        "Video interview with AI analysis",
        "Standardized evaluation frameworks"
      ],
      highlight: "2x better candidate quality"
    },
    {
      icon: <Zap className="w-12 h-12 text-yellow-600" />,
      title: "Faster Screening",
      description: "Reduce candidate screening time by leveraging AI-powered analysis and insights.",
      benefits: [
        "Automated resume parsing and scoring",
        "Quick candidate disqualification rules",
        "Bulk candidate processing capabilities"
      ],
      highlight: "70% faster screening"
    },
    {
      icon: <Search className="w-12 h-12 text-red-600" />,
      title: "Enhanced Talent Discovery",
      description: "Uncover hidden potential with advanced algorithms analyzing beyond traditional metrics.",
      benefits: [
        "Predictive success modeling based on past hires",
        "Passive candidate sourcing automation",
        "Advanced Boolean search capabilities"
      ],
      highlight: "3x larger talent pool"
    },
    {
      icon: <Users className="w-12 h-12 text-indigo-600" />,
      title: "Collaborative Hiring",
      description: "Enable teams to work together with shared candidate evaluations and interview feedback.",
      benefits: [
        "Real-time collaboration tools for hiring teams",
        "Structured interview scorecards",
        "Mobile app for instant feedback sharing"
      ],
      highlight: "40% faster decisions"
    }
  ];

  return (

    <div className="pt-16">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="text-center max-w-3xl mx-auto py-20 px-4">
          <p className="mt-2 text-4xl font-extrabold sm:text-5xl">Transforming Recruitment with Innovation</p>
          <p className="mt-6 text-xl text-blue-100">Our platform is designed to revolutionize hiring processes with cutting-edge features and AI-driven insights.</p>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="space-y-24">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={fadeInVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <div className="lg:grid lg:grid-cols-12 lg:gap-8">
                {/* Left Column */}
                <div className="lg:col-span-4">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-white rounded-xl shadow-md">{feature.icon}</div>
                    <h3 className="text-2xl font-bold text-gray-900">{feature.title}</h3>
                  </div>
                  <div className="mt-6 bg-blue-50 p-4 rounded-lg">
                    <div className="text-sm text-blue-600 font-medium">KEY METRIC</div>
                    <div className="text-2xl font-bold text-blue-900 mt-1">{feature.highlight}</div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="mt-6 lg:mt-0 lg:col-span-8">
                  <div className="bg-white rounded-xl p-8 ">
                    <p className="text-lg text-gray-600 mb-6">{feature.description}</p>
                    <div className="border-t pt-6">
                      <h4 className="font-semibold text-gray-900 mb-4">Core Capabilities:</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        {feature.benefits.map((benefit, idx) => (
                          <div key={idx} className="flex items-start">
                            <ArrowRight className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-600">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      {/* CTA Section */}
      <motion.div
        className="bg-blue-600 py-16"
        variants={fadeInVariant}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <div className="relative z-10 text-center max-w-3xl mx-auto">
              <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
              Ready to Experience the Future of Recruitment?
              </h2>
              <p className="mt-4 text-xl text-blue-100">
              Discover how our AI-powered platform can transform your hiring process. Try it today.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
                <a
                  href="/demo"
                  className="inline-flex items-center justify-center px-8 py-4 rounded-lg border-2 border-white text-white hover:bg-blue-700 font-medium transition-colors duration-300"
                >
                  Request Demo
                  <Rocket className="ml-2 h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      
    </div>
  );
};

export default Features;